import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessenger } from '../context/messengerContext';
import './AppBackButton.css';
import { useLocation } from 'react-router-dom';
import { navSections } from '../config/navSections';

/**
 * Универсальная кнопка "Назад":
 * - telegram / max: показывает нативную кнопку через showBackButton, в DOM ничего не рендерит.
 * - web: рендерит UI-кнопку со стрелкой влево.
 */

// Маршруты, на которых кнопка не отображается вообще.
const HIDE_BACK_ROUTES_PATHS = ['/', '/auth', '/auth_contact', '/install'];

// Пути верхнего уровня (прямо доступны из меню): назад всегда ведёт в /menu.
const TOP_LEVEL_PATHS = new Set(navSections.map((s) => s.path));

export default function AppBackButton() {
    const { t } = useTranslation('basic');
    const location = useLocation();
    const pathname = location.pathname || '';

    const { platform, showBackButton, hideBackButton, navigate, close } =
        useMessenger();

    const isMenu = pathname === '/menu';
    const shouldHide = HIDE_BACK_ROUTES_PATHS.some(
        (p) => pathname === p || pathname.startsWith(p + '/'),
    );

    const fromMenu = location.state?.fromMenu ?? false;
    const isTopLevel = TOP_LEVEL_PATHS.has(pathname);

    const onNavigate = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const fromChatQuery = urlParams.has('fromChat');

        if (fromChatQuery || fromMenu || isTopLevel) {
            navigate('/menu');
            return;
        }
        navigate(-1);
    };

    useEffect(() => {
        if (platform === 'web') return;

        if (shouldHide) {
            hideBackButton();
            return;
        }

        // На /menu нативная кнопка назад закрывает приложение.
        showBackButton(isMenu ? () => close() : () => onNavigate());
        return () => hideBackButton();
    }, [platform, showBackButton, hideBackButton, navigate, close, shouldHide, fromMenu, isTopLevel, isMenu]);

    if (shouldHide) return null;

    const isNativeSDK =
        window.Telegram?.WebApp?.initData ||
        window.TelegramWebviewProxy ||
        window.WebApp?.initData;

    if (isNativeSDK) return null;

    // Веб / MAX без SDK — рендерим кнопку сами.
    if (isMenu) {
        return (
            <button
                type='button'
                className='app-back-button'
                onClick={() => close()}
                aria-label={t('close')}
            >
                ×
            </button>
        );
    }

    return (
        <button
            type='button'
            className='app-back-button'
            onClick={() => onNavigate()}
            aria-label={t('back')}
        >
            ←
        </button>
    );
}
